import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaPlay, FaPause } from 'react-icons/fa';
import ApiService from '../ApiService';
import ImageWithToken from './ImageWithToken';
import Modal from './Modal';
import * as db from '../cache/db';
import { audioKey } from '../cache/routePreCache';
import { trackEvent } from '../analytics';


function StoryModal({user, storyModal, setStoryModal, closeStoryModal}) {
    const { t, i18n } = useTranslation();
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(null)

    const handleAudioPlayback = () => {
        if (storyModal.audioLoading) return;
        
        if (!storyModal.audio) {
            fetchPlaceAudio(storyModal.place);
        } else {
        if (storyModal.isPlaying) {
            storyModal.audio.pause();
            setStoryModal(prev => ({ ...prev, isPlaying: false}));
        } else {
            storyModal.audio.play().then(() => {
              trackEvent('audio_playback_started', { place_name: storyModal.place?.description?.name });
              setStoryModal(prev => ({ ...prev, isPlaying: true}));
            }).catch(error => {
              console.error('Error playing audio:', error);
            });
        }
        }
    };

const fetchPlaceAudio = useCallback(async (place) => {
    if (!place)
        return

    setStoryModal(prev => ({ ...prev, audioLoading: true }));

    try {
      const ref = place.reference;
      const lang = i18n.language;

      // Cache-first
      let audioBlob = await db.get(audioKey(ref, lang));
      if (!audioBlob) {
        audioBlob = await ApiService.post('/place/audio/', {
          place_id: place.id,
          place_ref: ref,
          language: lang,
        }, user, 'blob');
      }

      if (audioBlob) {
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);

        audio.addEventListener('loadedmetadata', () => {
          setDuration(audio.duration);
        })

        audio.addEventListener('timeupdate', () => {
          setCurrentTime(audio.currentTime);
        });
        
        audio.addEventListener('ended', () => {
          trackEvent('audio_playback_completed', { place_name: place?.description?.name });
          setStoryModal(prev => ({ ...prev, isPlaying: false }));
        });
        
        audio.addEventListener('error', (e) => {
          console.error('Audio playback error:', e);
          setStoryModal(prev => ({ ...prev, isPlaying: false}));
        });
        
        setStoryModal(prev => ({ ...prev, audio: audio, audioLoading: false}));

        if ("mediaSession" in navigator) {
          const photoUrl = await ApiService.create_place_photo_url_with_token(user, place);

          navigator.mediaSession.metadata = new MediaMetadata({
            title: storyModal.story?.description.title,
            artist: "WhereItHappened",
            album: storyModal.place?.description.name,
                artwork: [
            {
              src: `${photoUrl}&w=96&h=96`,
              sizes: "96x96",
              type: "image/jpeg",
            },
            {
              src: `${photoUrl}&w=128&h=128`,
              sizes: "128x128",
              type: "image/jpeg",
            },
            {
              src: `${photoUrl}&w=192&h=192`,
              sizes: "192x192",
              type: "image/jpeg",
            },
            {
              src: `${photoUrl}&w=256&h=256`,
              sizes: "256x256",
              type: "image/jpeg",
            },
            {
              src: `${photoUrl}&w=384&h=384`,
              sizes: "384x384",
              type: "image/jpeg",
            },
            {
              src: `${photoUrl}&w=512&h=512`,
              sizes: "512x512",
              type: "image/jpeg",
            },
          ],
          })
        }

        if (storyModal.autoplay) {
          audio.play().then(() => {
            trackEvent('audio_playback_started', { place_name: place?.description?.name });
            setStoryModal(prev => ({ ...prev, isPlaying: true}));
          }).catch(error => {
            console.error('Error playing audio:', error);
          });
        }
        
      } else {
        console.error('Failed to fetch audio');
        setStoryModal(prev => ({ ...prev, audioLoading: false }));
      }
    } catch (error) {
      console.error('Error fetching audio:', error);
      setStoryModal(prev => ({ ...prev, audioLoading: false }));
    }
  }, [setStoryModal, i18n.language, user, storyModal.autoplay, storyModal.story?.description.title, storyModal.place?.description.name]);

  const toTime = (seconds) => {
    if (!seconds){
        return 
    }
  var date = new Date(null);
  date.setSeconds(seconds);
  return date.toISOString().substring(14, 19);
}

useEffect(() => {
  if(!storyModal.loading)
    fetchPlaceAudio(storyModal.place);
}, [fetchPlaceAudio, storyModal.place, storyModal.loading])

return (
  <Modal isOpen={storyModal.isOpen} onClose={closeStoryModal} showClose={!storyModal.loading}>
    {storyModal.loading ? (
      <div className="story-loading">
        <FaSpinner className="spinning" />
        <p>{t('story_modal.loading')}</p>
      </div>
    ) : storyModal.story ? (
      <div className="story-content">
        <div className="story-header">
          {storyModal.place ? (
            <ImageWithToken imageUrl={ApiService.create_place_photo_url(storyModal.place)}
              alt={storyModal.place.description?.name || 'Place'}
              className="story-header-image"
              user={user}
            />
          ) : (
            <div className="story-header-placeholder">
              <div className="placeholder-text">
                {(storyModal.place?.description?.name || t('story_modal.unknown_place')).charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          <div className="story-header-content">
            <h1 className="story-title">
              {storyModal.place?.description?.name || t('story_modal.unknown_place')}
            </h1>
            <p className="story-subtitle">
              {storyModal.place?.description?.vicinity || t('story_modal.interesting_place')}
            </p>
          </div>
        </div>

        <div className="story-body">
          <div className="story-sections">
            <h3 className="section-content">
              <p>{storyModal.story?.description?.title}</p>
            </h3>
          </div>

          <div className="story-listen">
            <button
              className="listen-button"
              onClick={handleAudioPlayback}
              disabled={storyModal.audioLoading}
            >
              {storyModal.audioLoading ? (
                <FaSpinner className="spinning" />
              ) : storyModal.isPlaying ? (
                <FaPause />
              ) : (
                <FaPlay />
              )}
              <span>
                {storyModal.audioLoading
                  ? t('story_modal.loading_audio')
                  : storyModal.isPlaying
                    ? t('story_modal.pause_story', { currentTime: toTime(currentTime), duration: toTime(duration) })
                    : t('story_modal.listen_story', { duration: duration ? toTime(duration) : "" })
                }
              </span>
            </button>
          </div>

          <div className="story-text">
            {storyModal.story.description?.text?.split('\n\n').map((paragraph, index) => (
              <p key={index} className="story-paragraph">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>
    ) : (
      <div className="story-error">
        <p>{t('story_modal.error')}</p>
        <button onClick={closeStoryModal}>{t('story_modal.close')}</button>
      </div>
    )}
  </Modal>
  );
}

export default StoryModal;
